import { DiwaAPI } from './client'

/**
 * Commune — the town-square API. Three owner-scoped slices, one shared /commune prefix:
 *
 *   1. roadmap-votes — persisted community votes on roadmap items, keyed by the slug the
 *      frontend derives from content/roadmap.ts (see lib/commune.voteKey). `voted` is THIS
 *      caller's state; `count` is the whole community's tally. One vote per (user, key).
 *   2. keepers — the public directory (egos whose identity section is public) and one
 *      keeper's full profile, each section gated server-side by its own membrane flag.
 *   3. requests — the caller's own persisted source requests, newest first, with a status.
 *
 * The membrane lives on the server: this client asks, the server decides what crosses. A
 * keeper's goals are structurally private and never appear in any shape here.
 */

/** One roadmap item's tally + the caller's own vote state. */
export interface RoadmapVote {
  /** The whole community's vote count for this item. */
  count: number
  /** Did THIS caller vote for it. */
  voted: boolean
}

/** GET /commune/roadmap-votes — a map keyed by the roadmap item slug. */
export interface RoadmapVotesResponse {
  votes: Record<string, RoadmapVote>
}

/** A directory card — the quiet public shape (identity + optional sigil glyphs). */
export interface KeeperCard {
  id: string
  name: string
  /** One-line self-description (identity t0.tagline), or '' if unset. */
  tagline: string
  /** Earned sigil glyphs — present only if the keeper's sigils section is public; else []. */
  sigils: string[]
}

/** GET /commune/keepers — every identity-public keeper. */
export interface KeepersResponse {
  keepers: KeeperCard[]
}

/** A single earned sigil as it crosses the membrane — glyph + label, no state. */
export interface KeeperSigil {
  key: string
  name: string
  glyph: string
}

/** The compact 'work' view — only present if the keeper's progression section is public. */
export interface KeeperProgression {
  sigils_earned: number
  streak_days: number
  entities: number
}

/** The three membrane flags, echoed so the UI can honestly label what's shown/withheld. */
export interface KeeperVisibility {
  identity: string
  sigils: string
  progression: string
}

/**
 * GET /commune/keepers/{id} — the full profile, each section membrane-gated:
 *   - identity fields (name, tagline, professional_summary, expertise) — always present
 *     (the endpoint 404s unless identity is public).
 *   - sigils — [] unless the sigils section is public.
 *   - progression — null unless the progression section is public.
 *   - goals — NEVER present in any field (structurally private).
 */
export interface KeeperProfile {
  id: string
  name: string
  tagline: string
  professional_summary: string
  expertise: string[]
  sigils: KeeperSigil[]
  progression: KeeperProgression | null
  visibility: KeeperVisibility
}

/** One of the caller's persisted source requests. */
export interface SourceRequest {
  id: string
  /** 'source' today — the shape is future-proofed for other request kinds. */
  kind: 'source'
  name: string
  detail?: string | null
  /** e.g. 'received' | 'in_review' — defaults to 'received' when the row has no status yet. */
  status: string
  /** ISO timestamp. */
  created_at: string
}

/** GET /commune/requests — the caller's own requests, newest first. */
export interface RequestsResponse {
  requests: SourceRequest[]
}

export class CommuneAPI {
  private api: DiwaAPI

  constructor(baseUrl: string) {
    this.api = new DiwaAPI(baseUrl)
  }

  /** GET /commune/roadmap-votes?keys=a,b,c — tally + caller vote-state for the given slugs. */
  roadmapVotes(keys: string[]): Promise<RoadmapVotesResponse> {
    const q = encodeURIComponent(keys.join(','))
    return this.api.get<RoadmapVotesResponse>(`/commune/roadmap-votes?keys=${q}`)
  }

  /** POST /commune/roadmap-votes/{key} — toggle the caller's vote; returns {count, voted}. */
  toggleVote(key: string): Promise<RoadmapVote> {
    return this.api.post<RoadmapVote>(`/commune/roadmap-votes/${encodeURIComponent(key)}`, {})
  }

  /** GET /commune/keepers — the public directory. */
  keepers(): Promise<KeepersResponse> {
    return this.api.get<KeepersResponse>('/commune/keepers')
  }

  /** GET /commune/keepers/{id} — one keeper's membrane-respecting profile. */
  keeper(id: string): Promise<KeeperProfile> {
    return this.api.get<KeeperProfile>(`/commune/keepers/${encodeURIComponent(id)}`)
  }

  /** GET /commune/requests — the caller's own source requests. */
  requests(): Promise<RequestsResponse> {
    return this.api.get<RequestsResponse>('/commune/requests')
  }
}
